/**
 * Small controls shared by the Ruby AI tabs. Ruby on light (super-admin
 * theme), custom controls only, 44px touch targets. No native select /
 * confirm / date input anywhere in here.
 */
import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';

export const Card: React.FC<{ className?: string; children: React.ReactNode; dashed?: boolean }> = ({ className = '', children, dashed }) => (
  // Explicit ink: the admin shell inherits white text (legacy dark theme), so
  // any cell without its own colour would render white-on-white.
  <div className={`bg-white text-stone-800 border ${dashed ? 'border-dashed border-stone-300' : 'border-stone-200'} rounded-xl shadow-sm ${className}`}>
    {children}
  </div>
);

export const CardHeader: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; actions?: React.ReactNode }> = ({ title, hint, actions }) => (
  <div className="px-4 md:px-5 py-3 border-b border-stone-100 flex items-start justify-between gap-3 flex-wrap">
    <div className="min-w-0">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {hint && <p className="text-[12px] text-stone-600 mt-0.5">{hint}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

type BtnKind = 'primary' | 'secondary' | 'soft' | 'ghost';
const BTN: Record<BtnKind, string> = {
  primary: 'bg-[#DC143C] text-white hover:bg-[#9F1239] border border-transparent',
  secondary: 'bg-white text-stone-800 border border-stone-300 hover:bg-stone-50',
  soft: 'bg-[#FFF1F3] text-[#9F1239] border border-[#9F1239] hover:bg-rose-100',
  ghost: 'bg-transparent text-[#9F1239] border border-transparent hover:bg-rose-50',
};

export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; busy?: boolean }> = ({
  kind = 'secondary', busy, className = '', children, disabled, ...rest
}) => (
  <button
    type="button"
    {...rest}
    disabled={disabled || busy}
    className={`inline-flex items-center justify-center gap-2 px-4 min-h-[44px] rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BTN[kind]} ${className}`}
  >
    {busy && <RubyGemMark size={16} drawing color="currentColor" />}
    {children}
  </button>
);

/** Emerald on/off switch — 44px hit area around a 40×24 track. */
export const Toggle: React.FC<{
  on: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean; busy?: boolean;
}> = ({ on, onChange, label, disabled, busy }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled || busy}
    onClick={() => onChange(!on)}
    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] disabled:opacity-50"
  >
    {busy ? <RubyGemMark size={18} drawing /> : (
      <span className={`relative inline-block w-10 h-6 rounded-full transition-colors ${on ? 'bg-emerald-600' : 'bg-stone-300'}`}>
        <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white shadow transition-all ${on ? 'left-[19px]' : 'left-[3px]'}`} />
      </span>
    )}
  </button>
);

const BADGE: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-800',
  stone: 'bg-stone-100 text-stone-600',
  amber: 'bg-amber-100 text-amber-800',
  black: 'bg-black text-white',
  ruby: 'bg-[#FFF1F3] text-[#9F1239]',
  rubySolid: 'bg-[#DC143C] text-white',
  orange: 'bg-orange-50 text-orange-800',
  blue: 'bg-blue-50 text-blue-800',
  teal: 'bg-teal-50 text-teal-800',
};

export const Badge: React.FC<{ tone?: keyof typeof BADGE; children: React.ReactNode; className?: string; square?: boolean }> = ({
  tone = 'stone', children, className = '', square,
}) => (
  <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 ${square ? 'rounded-md' : 'rounded-full'} ${BADGE[tone]} ${className}`}>
    {children}
  </span>
);

export const FieldLabel: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[12px] text-stone-600 mb-1.5">{children}</label>
);

const INPUT = 'w-full px-3 min-h-[44px] text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 disabled:bg-stone-50';

export const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { suffix?: string }> = ({ suffix, className = '', ...rest }) => (
  <div className="relative">
    <input type="text" {...rest} className={`${INPUT} ${suffix ? 'pr-28' : ''} ${className}`} />
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-500 pointer-events-none">{suffix}</span>}
  </div>
);

export const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = '', ...rest }) => (
  <textarea {...rest} className={`w-full px-3 py-2.5 min-h-[88px] text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 ${className}`} />
);

export const Issues: React.FC<{ errors: string[]; warnings?: string[] }> = ({ errors, warnings = [] }) => {
  if (!errors.length && !warnings.length) return null;
  return (
    <div className="space-y-1.5">
      {errors.map((e) => (
        <p key={e} className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />{e}
        </p>
      ))}
      {warnings.map((w) => (
        <p key={w} className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />{w}
        </p>
      ))}
    </div>
  );
};

export const ErrorBanner: React.FC<{ title: string; detail?: string | null }> = ({ title, detail }) => (
  <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm">
    <p className="font-semibold">{title}</p>
    {detail && <p className="text-xs mt-0.5 break-words">{detail}</p>}
  </div>
);

/** Centred modal on light. Esc and the backdrop close it unless `busy`. */
export const Modal: React.FC<{
  open: boolean; title: string; subtitle?: React.ReactNode; onClose: () => void; busy?: boolean;
  footer?: React.ReactNode; children: React.ReactNode; wide?: boolean;
}> = ({ open, title, subtitle, onClose, busy, footer, children, wide }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-white rounded-2xl border border-stone-200 shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-stone-100">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-stone-900">{title}</h3>
            {subtitle && <div className="text-[12px] text-stone-600 mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={busy}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-stone-500 hover:text-stone-800 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-stone-100 flex justify-end gap-2 flex-wrap">{footer}</div>}
      </div>
    </div>
  );
};

/** Label/value line for breakdowns. */
export const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0 text-sm ${strong ? 'font-semibold text-stone-900' : 'text-stone-700'}`}>
    <span className="text-stone-600">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);

export const Mono: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`font-mono text-[12px] ${className}`}>{children}</span>
);
